import { useTranslation } from 'react-i18next';
import { useEventosOS } from '@/hooks/useEventosOS';
import {
  getEventoOSLabels,
  TipoEventoOS,
} from '@/types/ordemServico';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  FilePlus, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  UserCog, 
  UserCheck,
  MessageSquare,
  Ban,
  CheckCheck,
  Loader2,
  CalendarClock,
  Trash2,
  RotateCcw,
} from 'lucide-react';

interface HistoricoEventosOSProps {
  ordemServicoId: string;
}

const iconesPorTipo: Record<TipoEventoOS, React.ElementType> = {
  os_criada: FilePlus,
  os_aceita: CheckCircle,
  os_recusada: XCircle,
  status_alterado: RefreshCw,
  prestador_alterado: UserCog,
  responsavel_alterado: UserCheck,
  observacao_adicionada: MessageSquare,
  os_cancelada: Ban,
  os_concluida: CheckCheck,
  os_reagendada: CalendarClock,
  os_excluida: Trash2,
  os_reaberta: RotateCcw,
};

export function HistoricoEventosOS({ ordemServicoId }: HistoricoEventosOSProps) {
  const { t } = useTranslation();
  const EVENTO_OS_LABELS = getEventoOSLabels(t);
  const { data: eventos, isLoading } = useEventosOS(ordemServicoId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!eventos || eventos.length === 0) {
    return (
      <p className="text-center text-sm text-muted-foreground py-8">
        {t('orders.events.noEvents')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {eventos.map((evento, index) => {
        const Icone = iconesPorTipo[evento.tipo] || RefreshCw;

        return (
          <div key={evento.id} className="flex gap-3">
            {/* Linha do tempo — marcador monocromático */}
            <div className="flex flex-col items-center">
              <div className="h-8 w-8 rounded-full flex items-center justify-center bg-muted text-foreground">
                <Icone className="h-4 w-4" />
              </div>
              {index < eventos.length - 1 && (
                <div className="w-0.5 flex-1 bg-border-soft mt-2" />
              )}
            </div>

            {/* Conteúdo */}
            <div className="flex-1 pb-4">
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {EVENTO_OS_LABELS[evento.tipo]}
                </span>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(evento.criado_em), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              </div>
              
              <p className="text-sm text-muted-foreground mt-1">
                {evento.descricao}
              </p>
              
              {evento.usuario && (
                <p className="text-xs text-muted-foreground mt-1">
                  por {evento.usuario.nome}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
